/**
 * Utilitaires de composition datée d'une CustomView.
 *
 * L'ordre est porté par l'API. Le SDK n'en a besoin que pour deux choses : comparer la liste
 * recalculée à celle qu'il a en mémoire — ce qui exige le MÊME ordre des deux côtés, départage
 * compris — et appliquer sa borne de composition. Toute divergence de règle entre ici et l'API
 * ferait réécrire la vue entière à chaque session, sans rien corriger.
 */

import { CustomViewSortOrder } from "./types/customViews";
import type { CustomViewMailEntry } from "./types/customViews";

/**
 * Date exploitable, ou `null`. Une date malformée existe dans la nature (le tri de MailManager
 * la gère explicitement) ; sans cette garde, `toISOString()` lève un `RangeError` au moment de
 * sérialiser la charge utile, ce qui ferait échouer tout le recalcul de la vue.
 */
export const toValidDate = (value: unknown): Date | null => {
    if (value === null || value === undefined) return null;

    const date = value instanceof Date ? value : new Date(value as string);

    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * ⚠️ Fichier miroir : `Mailvista-Api-Backend/src/functions/domains/customView/services/customViewMailOrdering.ts`
 * La règle d'ordre DOIT être identique des deux côtés : toute divergence fait réécrire la vue
 * entière à chaque session. Tout changement ici doit être porté là-bas, et réciproquement.
 *
 * Départage par points de code, PAS `localeCompare` : ce dernier dépend de la locale et de la
 * présence d'ICU dans le runtime — sous Hermes sans ICU, « B » précède « a » ; avec ICU, l'inverse.
 * Les identifiants de message sont couramment à casse mixte : une simple égalité de date suffirait
 * alors à faire diverger l'ordre du SDK de celui de l'API.
 */
const byInternetMessageId = (
    a: CustomViewMailEntry,
    b: CustomViewMailEntry,
): number =>
    a.internetMessageId < b.internetMessageId
        ? -1
        : a.internetMessageId > b.internetMessageId
          ? 1
          : 0;

/**
 * Même règle que `sortCustomViewMails` côté API : les mails datés d'abord, selon le sens
 * d'affichage ; les mails sans date en fin de liste dans les deux sens ; départage sur
 * l'identifiant croissant, lui aussi dans les deux sens.
 */
export const sortMailEntries = (
    entries: readonly CustomViewMailEntry[],
    sortOrder: CustomViewSortOrder,
): CustomViewMailEntry[] => {
    // Dates normalisées UNE fois : les valider dans le comparateur coûtait quatre conversions
    // par comparaison, soit des dizaines de milliers d'appels sur une vue pleine.
    const normalized = entries.map((entry) => ({
        ...entry,
        sentDateTime: toValidDate(entry.sentDateTime),
    }));

    const dated = normalized.filter((entry) => entry.sentDateTime !== null);
    const undated = normalized.filter((entry) => entry.sentDateTime === null);

    const sortedDated = dated.sort((a, b) => {
        const aTime = (a.sentDateTime as Date).getTime();
        const bTime = (b.sentDateTime as Date).getTime();

        if (aTime !== bTime) {
            return sortOrder === CustomViewSortOrder.ASC
                ? aTime - bTime
                : bTime - aTime;
        }

        return byInternetMessageId(a, b);
    });

    return [...sortedDated, ...undated.sort(byInternetMessageId)];
};
